import React, { useState, useEffect } from 'react';

import { Box, Divider, Flex } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { useCalendarAuthDetails } from 'screens/admin/CheckIn/CheckInCalendar/hooks/dataHooks';
import { CalendarDisconnectionModal } from 'screens/oneOnOneRabbit/InstancePage/common/modals/CalendarDisconnectionModal';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { Disconnect, EditCalendar, Google, Outlook, Refresh2 } from 'assets/assetsV1/iconComponents';

import { getQueryParams } from 'constants/helper';
import { sendClickedOnOneOnOneCalenderIntegration, sendSyncOneOnOneCalendarEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { deactivateCalendarPluginV2, getCalendarAuthCodeV2, getCalendarAuthURLsV2 } from 'services/plugins';

import { Button } from 'uiToolkitV1/Button';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextBodyLarge, TextBodyMedium } from 'uiToolkitV1/Typography';

import { SettingsCardTemplate } from '../../components/common/SettingsCardTemplate';

const CalendarSettings = () => {
  const location = useLocation();
  const [isCalendarDisconnectionModalOpen, setIsCalendarDisconnectionModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const { search } = location;

  const { code, error, error_description, calendarType } = getQueryParams(search);

  const [authStatusFetched, setAuthStatusFetched] = useState(false);

  const { data, isLoading } = useCalendarAuthDetails({ authStatusFetched });

  const [isSettingAuthStatus, setIsSettingAuthStatus] = useState(false);

  const refetchHandler = () => {
    queryClient.invalidateQueries([REACT_QUERY_KEYS.settings.oneOnOne.SettingsOneOnOneCalendarAuth]);
  };

  const getAuthURLForIntegration = async ({ calendarType }) => {
    try {
      const res = await getCalendarAuthURLsV2({ calendarType });
      window.location = res;
    } catch (err) {
      console.error('Error authenticating with calendar service : ', err);
    }
  };

  const disconnectCalender = async () => {
    setLoading(true);
    setIsCalendarDisconnectionModalOpen(false);

    try {
      const res = await deactivateCalendarPluginV2();
      refetchHandler();
    } catch (err) {
      console.error('Could not remove the integration! Please try again.:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendCalendarAuthStatus = async () => {
    try {
      setIsSettingAuthStatus(true);
      const res = await getCalendarAuthCodeV2({
        calendarType,
        code,
        error,
        error_description,
      });
    } catch (error) {
      console.error('Error setting authentication status with calendar service : ', error);
    } finally {
      setAuthStatusFetched(true);
      setIsSettingAuthStatus(false);
    }
  };

  useEffect(() => {
    if (code || error) {
      sendCalendarAuthStatus();
    }
  }, [code, error, error_description, calendarType]);

  return (
    <Box m={!checkNewNavEnabled() ? 4 : 0}>
      <SettingsCardTemplate
        heading={!checkNewNavEnabled() && 'Calendar Settings'}
        subHeading=""
        Icon={EditCalendar}
        showDivider={!checkNewNavEnabled()}
      >
        {isLoading && <SkeletonLoader />}
        {!isLoading &&
          (data?.calendarType ? (
            <Flex mt="16px" flexDir="column" ml="14px">
              {data?.calendarType === 'GOOGLE' && (
                <Flex alignItems="center">
                  <Google boxSize="24px" mr="16px" />
                  <TextBodyMedium bold>Google Calendar • &nbsp;</TextBodyMedium>
                  <TextBodyMedium bold color="#44812F">
                    Active
                  </TextBodyMedium>
                  <Button
                    ml="auto"
                    variant="outlined"
                    onClick={() => {
                      sendClickedOnOneOnOneCalenderIntegration({
                        // Mixpanel Event
                        eventProperties: {
                          value: 'Disconnect',
                        },
                      });
                      setIsCalendarDisconnectionModalOpen(true);
                    }}
                    color="red"
                    isDisabled={isSettingAuthStatus || loading}
                    StartIcon={Disconnect}
                  >
                    Disconnect
                  </Button>
                </Flex>
              )}

              {data?.calendarType === 'OUTLOOK' && (
                <Flex alignItems="center">
                  <Outlook boxSize="24px" mr="16px" />
                  <TextBodyMedium bold>Microsoft Outlook • &nbsp;</TextBodyMedium>
                  <TextBodyMedium bold color="#44812F">
                    Active
                  </TextBodyMedium>
                  <Button
                    ml="auto"
                    variant="outlined"
                    onClick={() => {
                      sendClickedOnOneOnOneCalenderIntegration({
                        // Mixpanel Event
                        eventProperties: {
                          value: 'Connect',
                        },
                      });
                      setIsCalendarDisconnectionModalOpen(true);
                    }}
                    color="red"
                    isDisabled={isSettingAuthStatus || loading}
                    StartIcon={Disconnect}
                  >
                    Disconnect
                  </Button>
                </Flex>
              )}
            </Flex>
          ) : (
            <>
              <Flex flexDir="column" ml="14px">
                <TextBodyLarge bold>Sync your calendar with Mesh</TextBodyLarge>
                <TextBodyMedium>Select any calendar below to effortlessly manage your 1:1s</TextBodyMedium>
              </Flex>
              <Flex mt="16px" flexDir="column" ml="14px">
                <Flex alignItems="center">
                  <Google boxSize="24px" mr="16px" />
                  <TextBodyMedium bold>Google Calendar</TextBodyMedium>
                  <Button
                    ml="auto"
                    variant="outlined"
                    onClick={() => {
                      sendClickedOnOneOnOneCalenderIntegration({
                        // Mixpanel Event
                        eventProperties: {
                          value: 'Connect',
                        },
                      });
                      sendSyncOneOnOneCalendarEvent({
                        eventProperties: {
                          type: 'Google',
                        },
                      });
                      getAuthURLForIntegration({ calendarType: 'GOOGLE' });
                    }}
                    isDisabled={isSettingAuthStatus}
                    StartIcon={Refresh2}
                  >
                    Sync
                  </Button>
                </Flex>
                <Divider my="12px" color="#EBEFF3" opacity={1} />
                <Flex alignItems="center">
                  <Outlook boxSize="24px" mr="16px" />
                  <TextBodyMedium bold>Microsoft Outlook</TextBodyMedium>
                  <Button
                    ml="auto"
                    variant="outlined"
                    onClick={() => {
                      sendClickedOnOneOnOneCalenderIntegration({
                        // Mixpanel Event
                        eventProperties: {
                          value: 'Connect',
                        },
                      });
                      sendSyncOneOnOneCalendarEvent({
                        eventProperties: {
                          type: 'Outlook',
                        },
                      });
                      getAuthURLForIntegration({ calendarType: 'OUTLOOK' });
                    }}
                    isDisabled={isSettingAuthStatus}
                    StartIcon={Refresh2}
                  >
                    Sync
                  </Button>
                </Flex>
              </Flex>
            </>
          ))}
      </SettingsCardTemplate>
      <CalendarDisconnectionModal
        isCentered
        isOpen={isCalendarDisconnectionModalOpen}
        onClose={() => setIsCalendarDisconnectionModalOpen(false)}
        onSave={disconnectCalender}
        calendarType={data?.calendarType}
      />
    </Box>
  );
};

export { CalendarSettings };
